import React, { FC, ReactNode } from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { FiCopy, FiCheck } from 'react-icons/fi';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { getDateTime } from '@/utils/dateTime';

// ─── Variant ────────────────────────────────────────────────────────────────
/**
 * Visual style of the cell text.
 *   default   — inherits parent colour
 *   muted     — light grey, for secondary / timestamp info
 *   mono      — monospace font, for IDs / codes
 *   blur      — blurred until hovered (sensitive data)
 *   primary   — accent colour on hover
 */
export type CellVariant = 'default' | 'muted' | 'mono' | 'blur' | 'primary';

const VARIANT_CLASS: Record<CellVariant, string> = {
  default: 'text-gray-800 dark:text-gray-100',
  muted: 'text-gray-500 dark:text-gray-400',
  mono: 'font-mono hover:text-[var(--color-primary)] tracking-tight text-gray-700 dark:text-gray-300',
  blur: 'blur-sm hover:blur-none transition-all duration-300 select-none hover:select-auto',
  primary: 'hover:text-[var(--color-primary)] transition-colors',
};

// ─── Format ──────────────────────────────────────────────────────────────────
/**
 * Auto-format applied to `value` before display.
 *   none      — display as-is (default)
 *   datetime  — runs getDateTime(), shows "—" if empty
 *   currency  — prefixes "$", colours green when positive
 */
export type CellFormat = 'none' | 'datetime' | 'currency';

function applyFormat(
  value: string,
  format: CellFormat,
): { display: ReactNode; formatClass: string } {
  if (format === 'datetime') {
    return { display: value ? getDateTime(value) : '—', formatClass: '' };
  }
  if (format === 'currency') {
    const num = parseFloat(value);
    const positive = !isNaN(num) && num > 0;
    return {
      display: positive ? `$${value}` : value || '—',
      formatClass: positive
        ? 'font-semibold text-emerald-600 dark:text-emerald-400'
        : 'text-gray-500 dark:text-gray-400',
    };
  }
  return { display: value || '—', formatClass: '' };
}

// ─── Copy config ─────────────────────────────────────────────────────────────
export interface CellCopyConfig {
  /** Unique key scoped to the row — matched against `isCopy` state */
  copyKey: string;
  /** Active copy key from parent state */
  isCopy: string;
  /** Parent handler — sets isCopy and schedules reset */
  onCopyClick: (key: string) => void;
}

// ─── Props ───────────────────────────────────────────────────────────────────
export interface CellProps {
  /** Raw string value — used as display text, tooltip content and clipboard payload */
  value?: string;
  /** Visual style (default: "default") */
  variant?: CellVariant;
  /**
   * Auto-formatting applied before display.
   * Raw `value` is still used for copy / tooltip.
   */
  format?: CellFormat;
  /** Font-size class, e.g. "text-[12px]" (default: "text-[12px]") */
  size?: string;
  /**
   * Copy-to-clipboard config.
   * Omit entirely if the cell should not be copyable.
   */
  copy?: CellCopyConfig;
  /**
   * Tooltip behaviour:
   *   true        — show `value` as tooltip text
   *   string      — show custom tooltip text
   *   ReactNode   — show arbitrary rich content
   *   false/omit  — no tooltip
   */
  tooltip?: boolean | string | ReactNode;
  /** Tooltip placement (default: "right") */
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  /** Labeled tooltip: small all-caps label shown above the value */
  tooltipTitle?: string;
  /** Labeled tooltip: primary value shown below `tooltipTitle` */
  tooltipValue?: string;
  /** Extra Tailwind classes applied to the outermost span */
  className?: string;
  /** Override display content — value is still used for copy / tooltip */
  children?: ReactNode;
  /** data-testid for automation — assigned to the root span */
  testId?: string;
  /**
   * Optional secondary text rendered below the primary line.
   * Useful for showing a full name under a short name, or a subtype.
   */
  subtitle?: string;
  /** Variant for the subtitle line (default: "muted") */
  subtitleVariant?: CellVariant;
  /**
   * Arbitrary content slot rendered below subtitle — icons, badges, counts etc.
   * Rendered as-is; no additional styling applied.
   */
  meta?: ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────────────
const Cell: FC<CellProps> = ({
  value = '',
  variant = 'default',
  format = 'none',
  copy,
  tooltip,
  tooltipPlacement = 'top',
  tooltipTitle,
  tooltipValue,
  size = 'text-[12px]',
  className = '',
  children,
  testId,
  subtitle,
  subtitleVariant = 'muted',
  meta,
}) => {
  const copied = copy ? copy.isCopy === copy.copyKey : false;

  const handleClick = copy
    ? (e: React.MouseEvent) => {
      e.stopPropagation();
      copyToClipboard(value);
      copy.onCopyClick(copy.copyKey);
    }
    : undefined;

  const { display, formatClass } = applyFormat(value, format);
  const tooltipText: ReactNode | null =
    tooltip === true ? value
      : tooltip === false || tooltip == null ? null
        : tooltip;

  const hasExtras = Boolean(subtitle || meta);

  // Primary row: text + optional copy icon
  const primaryRow = (
    <span className={(hasExtras || copy) ? 'flex items-center w-full min-w-0' : undefined}>
      <span className="block truncate min-w-0">{children ?? display}</span>
      {copy && (
        <span className="flex-shrink-0 ml-1 opacity-0 group-hover/cell:opacity-100 transition-opacity duration-150">
          {copied ? (
            <FiCheck className="w-3 h-3 text-emerald-500" />
          ) : (
            <FiCopy className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          )}
        </span>
      )}
    </span>
  );

  const inner = (
    <span
      data-testid={testId ?? ''}
      className={[
        'group/cell relative flex w-full min-w-0',
        hasExtras ? 'flex-col' : 'items-center',
        size,
        copy ? 'cursor-pointer' : '',
        // currency formatClass overrides variant colour
        format === 'currency' ? formatClass : VARIANT_CLASS[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {primaryRow}

      {/* Subtitle line */}
      {subtitle && (
        <span
          className={`block truncate min-w-0 text-[11px] mt-0.5 ${VARIANT_CLASS[subtitleVariant]
            }`}
        >
          {subtitle}
        </span>
      )}

      {/* Meta slot — icons, badges, counts */}
      {meta && <span className="block mt-0.5">{meta}</span>}
    </span>
  );

  if (tooltipText || tooltipTitle) {
    return (
      <Tooltip
        content={tooltipText as ReactNode || undefined}
        title={tooltipTitle}
        value={tooltipValue ?? (tooltipText as string | undefined)}
        placement={tooltipPlacement}
      >
        {inner}
      </Tooltip>
    );
  }

  return inner;
};

export default Cell;
